import { useSyncExternalStore } from 'react';
import type { TimeKind } from '@/features/bet-inspection/components/sections/DataTable';
import type { SourceKey } from '@/types';

/** The four transaction stores the view reconciles. */
export type TxSourceKey = Extract<
  SourceKey,
  'transactions' | 'clickhouseRealtime' | 'clickhouseMv' | 'clickhouseSync'
>;

/** Result field name for each source (the primary DB is `mysql`, not `transactions`). */
export const TX_SOURCES: {
  key: TxSourceKey;
  /** Field on TransactionsResult. */
  field: 'mysql' | 'clickhouseRealtime' | 'clickhouseMv' | 'clickhouseSync';
  label: string;
  glossary: string;
  timeKind: TimeKind;
}[] = [
  { key: 'transactions', field: 'mysql', label: 'Primary Database', glossary: 'source_transactions', timeKind: 'mysql' },
  { key: 'clickhouseRealtime', field: 'clickhouseRealtime', label: 'Analytics — realtime', glossary: 'source_clickhouseRealtime', timeKind: 'clickhouse' },
  { key: 'clickhouseMv', field: 'clickhouseMv', label: 'Analytics — attempt log', glossary: 'source_clickhouseMv', timeKind: 'clickhouse' },
  { key: 'clickhouseSync', field: 'clickhouseSync', label: 'Analytics — settled', glossary: 'source_clickhouseSync', timeKind: 'clickhouse' },
];

const ALL: TxSourceKey[] = TX_SOURCES.map((s) => s.key);

/**
 * Which sources are shown — a MODULE-LEVEL store so the choice survives switching
 * to another console view and back (it lives in memory, not the component tree).
 * At least one source is always selected. Default: all four.
 */
let snapshot: TxSourceKey[] = ALL;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export function toggleTxSource(key: TxSourceKey): void {
  const set = new Set(snapshot);
  if (set.has(key)) {
    if (set.size > 1) set.delete(key); // never let the last one be turned off
  } else {
    set.add(key);
  }
  snapshot = ALL.filter((k) => set.has(k)); // stable order + new reference
  emit();
}

export function useSelectedTxSources(): TxSourceKey[] {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => snapshot,
    () => snapshot,
  );
}
